// Visual clearcoat picker for the permanent-flow clearcoat axis.
// Two tiles: Standard clearcoat vs VHS Crystal upgrade. Mirrors the
// PaintConditionGallery / SizeSilhouetteGallery shape so the full
// permanent-flow reads as one consistent visual experience.

import { useState } from "react"

interface ClearcoatChip {
  value: string
  label: string
  helperText?: string
}

interface Props {
  chips: ClearcoatChip[]
  priceDelta?: Record<string, number>
  onSelect: (chipLabel: string) => void
}

const CLEARCOAT_IMAGE_URLS: Record<string, string> = {
  standard:
    "https://cdn.shopify.com/s/files/1/0973/6008/files/Standard_5f2ffa70-3c0a-4173-85f3-fdcff7ad3247.webp",
  vhs: "https://cdn.shopify.com/s/files/1/0973/6008/files/VHS_e5b71bb9-308a-4dcc-9e19-1be605e3d330.webp",
}

function imageUrlFor(value: string): string | null {
  return CLEARCOAT_IMAGE_URLS[value.trim().toLowerCase()] ?? null
}

export function ClearcoatGallery({ chips, priceDelta, onSelect }: Props) {
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          opacity: 0.6,
          marginBottom: 10,
          textAlign: "center",
        }}
      >
        Tap a clearcoat to pick
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        {chips.map((c) => {
          const imageUrl = imageUrlFor(c.value)
          const delta = priceDelta?.[c.value]
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => onSelect(c.label)}
              title={c.helperText}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
                padding: "10px 10px 8px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "inherit",
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
                transition:
                  "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(109, 195, 247, 0.12)"
                e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.55)"
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(109,195,247,0.18)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.05)"
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"
                e.currentTarget.style.boxShadow = "none"
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  background:
                    "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 8,
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                  overflow: "hidden",
                }}
              >
                <ClearcoatImageOrPlaceholder
                  imageUrl={imageUrl}
                  label={c.label}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13 }}>{c.label}</span>
                {delta != null && (
                  <span style={{ opacity: 0.7, fontSize: 11.5 }}>
                    +${(delta / 100).toFixed(0)}
                  </span>
                )}
              </div>
              {c.helperText && (
                <span
                  style={{ opacity: 0.65, fontSize: 11.5, lineHeight: 1.3 }}
                >
                  {c.helperText}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ClearcoatImageOrPlaceholder({
  imageUrl,
  label,
}: {
  imageUrl: string | null
  label: string
}) {
  const [failed, setFailed] = useState(false)
  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    )
  }
  return (
    <svg
      viewBox="0 0 64 64"
      style={{ width: "55%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <rect x="18" y="14" width="28" height="38" rx="3" fill="currentColor" />
      <rect x="22" y="8" width="20" height="8" rx="1" fill="currentColor" />
    </svg>
  )
}
